import {
  TestIssueService,
  ITestIssueSchema,
  testIssueSchema,
} from '@/api/testIssue';
import { IDetailTestRunSchema } from '@/api/testRunDetail';
import { Loading } from '@/components/Loading';
import { Section } from '@/components/Section';
import { FormContainer, FormFooter } from '@/components/form/FormContainer';
import { OutlineButton } from '@/components/shared/button/OutlineButton';
import { AttachmentForm } from '@/components/testIssue/AttachmentForm';
import { TestIssueForm } from '@/components/testIssue/TestIssueForm';
import { useBackNavigate } from '@/utils/hooks/useBackNavigate';
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Dialog } from 'alurkerja-ui';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

export const CreateEditTestIssue: React.FC<{
  backAction?: () => void;
  onSuccess?: () => void;
  testRunDetail?: IDetailTestRunSchema;
}> = ({ backAction, testRunDetail, onSuccess }) => {
  const { id, testIssueId } = useParams();
  const backNavigate = useBackNavigate();

  // get data detail
  const { data: testIssueData, isFetching: isTestIssueFetching } =
    TestIssueService.useGet(testIssueId);

  const { data: optionData, isFetching: isOptionsFetching } =
    TestIssueService.useGetDropDown(id);
  const { mutate: create, isPending: isCreateLoading } =
    TestIssueService.useCreate();
  const { mutate: update, isPending: isUpdateLoading } =
    TestIssueService.useUpdate();

  const isFetching = isTestIssueFetching || isOptionsFetching;
  const isLoading = isCreateLoading || isUpdateLoading;

  const form = useForm<ITestIssueSchema>({
    resolver: zodResolver(testIssueSchema),
    values: testIssueData,
    defaultValues: {
      status: testIssueData?.status ?? 'NEW',
      module_id: testRunDetail?.test_case?.module_id,
    },
  });

  const submitAction = (data: ITestIssueSchema) => {
    data.project_id = id;

    if (testIssueId) {
      update(data, {
        onSuccess: () => {
          Dialog.success({
            title: 'Success',
            description: 'Test issue has been updated',
            callback: () => (backAction ? backAction() : backNavigate()),
          });
        },
        onError: (error: any) => {
          Dialog.error({
            title: 'Error',
            description:
              error?.response?.data?.message ?? 'Failed to update test issue',
          });
        },
      });
    } else {
      create(
        { data, testRunDetail },
        {
          onSuccess: () => {
            Dialog.success({
              title: 'Success',
              description: 'Test issue has been created',
              callback: () => (onSuccess ? onSuccess() : backNavigate()),
            });
          },
          onError: (error: any) => {
            Dialog.error({
              title: 'Error',
              description:
                error?.response?.data?.message ?? 'Failed to create test issue',
            });
          },
        },
      );
    }
  };

  const toBack = () => (backAction ? backAction() : backNavigate());

  if (isFetching) {
    return (
      <Section>
        <Loading />
      </Section>
    );
  }

  return (
    <TabGroup className="tab-group">
      <TabList className="tab-list">
        <Tab className="tab-button">Details</Tab>
        <Tab className="tab-button">Attachments</Tab>
      </TabList>

      <FormContainer form={form} onSubmit={submitAction}>
        <Section
          title={testIssueId ? 'Update Test Issue' : 'Create Test Issue'}
        >
          <TabPanels>
            <TabPanel>
              <TestIssueForm
                optionsData={optionData}
                testRunDetailData={testRunDetail}
              />
            </TabPanel>

            <TabPanel>
              <AttachmentForm />
            </TabPanel>
          </TabPanels>
        </Section>

        <FormFooter className="tab-list">
          <OutlineButton onClick={toBack}>Back</OutlineButton>

          <Button loading={isLoading}>
            {testIssueId ? 'Update' : 'Create'}
          </Button>
        </FormFooter>
      </FormContainer>
    </TabGroup>
  );
};
